import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Sidebar } from './Sidebar';
import { Navigation } from './Navigation';
import { TopNav } from './TopNav';
import { ChatCoach } from '../ui/ChatCoach';
import { Icons } from '@shared/constants';
import { useAuth } from '../../context/AuthContext';
import { useDemoMode } from '../../demo/DemoContext';
import { useAiPreference } from '../../hooks/useAiPreference';
import { useHideOnScroll } from '../../hooks/useHideOnScroll';
import { useRemoteConfig } from '../../context/RemoteConfigContext';
import { AnnouncementBanners } from '../ui/AnnouncementBanner';
import { UpdateBanner } from '../ui/UpdateBanner';
import { useVersionCheck } from '../../hooks/useVersionCheck';
import { useFullscreenLoading } from '../../context/FullscreenLoadingContext';
import { PageHeaderProvider } from '../../context/PageHeaderContext';
import { recordFeatureUseEvent } from '@shared/services/analyticsService';
import { Capacitor } from '@capacitor/core';

interface ResponsiveLayoutProps {
  children: React.ReactNode;
}

export const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { routePrefix } = useDemoMode();
  const { aiEnabled } = useAiPreference();
  const { tier, isNative } = useVersionCheck();
  // 'hard' never reaches here — App renders the blocking gate instead of the router.
  const updateAvailable = tier === 'soft';
  const { isFullscreenLoading } = useFullscreenLoading();
  // Scroll-away chrome: the chat button ducks with the bottom nav while the
  // user scrolls down the page and returns on scroll up (but never while the
  // chat panel is open — the toggle must stay reachable).
  const chromeHidden = useHideOnScroll();
  const [isChatOpen, setIsChatOpen] = useState(false);
  // Once opened, the chat stays mounted (hidden via CSS when closed) so the
  // conversation survives closing the panel and navigating the app —
  // including taps on the deep links the coach itself suggests.
  const [chatHasOpened, setChatHasOpened] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [viewportStyle, setViewportStyle] = useState<React.CSSProperties | undefined>(undefined);
  // Tailwind `lg` breakpoint — below it the chat is a full-screen overlay
  // pinned to the visual viewport; at/above it a floating panel.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (isChatOpen) setChatHasOpened(true);
  }, [isChatOpen]);

  // Feature adoption: chat coach opens (throttled to once per tab session)
  useEffect(() => {
    if (isChatOpen && currentUser) {
      recordFeatureUseEvent(currentUser.uid, 'chatcoach', 'open').catch(() => {});
    }
  }, [isChatOpen, currentUser]);

  // Track visual viewport to keep chat pinned to visible area above the keyboard.
  // In Capacitor, keyboard resize: 'native' resizes the webview itself, so
  // fixed positioning works naturally — skip visual viewport tracking there.
  // In browsers (Safari/Chrome), the visual viewport API is needed because
  // fixed elements stay relative to the layout viewport, not the visual one.
  useEffect(() => {
    if (!isChatOpen) {
      setViewportStyle(undefined);
      return;
    }
    // In Capacitor, resize: 'native' shrinks the webview — fixed inset:0 just works.
    if (Capacitor.isNativePlatform()) return;

    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      setViewportStyle({
        position: 'fixed',
        top: `${vv.offsetTop}px`,
        left: 0,
        width: '100%',
        height: `${vv.height}px`,
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isChatOpen]);

  // Derive current view from pathname for profile button highlighting
  const currentView = location.pathname.slice(1) || 'home';

  // Hide chat coach FAB/panel on profile (settings-type pages) or when AI is disabled
  const chatDisabled = !aiEnabled;
  const chatHiddenOnPage = ['/profile', `${routePrefix}/profile`].includes(location.pathname);

  const handleProfileClick = () => {
    navigate(`${routePrefix}/profile`);
  };

  return (
    <PageHeaderProvider>
    <div className="min-h-screen bg-sand font-sans text-growth selection:bg-foliage/20">
      {/* Update / Announcement Banners — full width above everything */}
      {updateAvailable && <UpdateBanner isNative={isNative} />}
      <AnnouncementBanners hasBannerAbove={updateAvailable} />

      {/* Desktop Layout: Sidebar + Content */}
      <div className="flex">
        {/* Desktop Sidebar - hidden on mobile, visible on lg+ */}
        <Sidebar />

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 min-h-screen overflow-x-clip">
          {/* Top Navigation - different behavior on mobile vs desktop */}
          <div className="lg:hidden">
            <TopNav />
          </div>

          {/* Desktop Header - simplified for desktop */}
          <header className="hidden lg:flex sticky top-0 z-40 items-center justify-between px-8 py-4 bg-sand/90 ">
            <div className="flex items-center gap-4">
              {/* <h1 className="text-lg font-serif text-growth capitalize">{currentView}</h1> */}
            </div>
            <div className="flex items-center gap-4">
              {/* Desktop-specific header actions can go here */}
              <button
                onClick={handleProfileClick}
                className="p-2 text-growth hover:bg-growth/5 rounded-full transition-colors"
                aria-label="Profile"
              >
                {currentUser?.photoURL && !avatarError ? (
                  <img
                    src={currentUser.photoURL}
                    alt={currentUser.displayName || 'Profile'}
                    className="w-8 h-8 rounded-full object-cover border border-mist/30"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                )}
              </button>
            </div>
          </header>

          {/* Content Container */}
          <div className="lg:px-8 lg:py-6 min-w-0 overflow-x-clip">
            {/* Mobile: Full width with padding */}
            {/* Desktop: Max width container with more breathing room */}
            <div className="min-w-0 lg:max-w-4xl xl:max-w-5xl">
              <div className="min-w-0 pt-2 px-4 lg:px-0 pb-28 lg:pb-8 animate-[fadeIn_1s_ease-out]">
                {children}
              </div>
            </div>
          </div>

          {/* Mobile Navigation - visible on mobile, hidden on lg+ and during fullscreen loading */}
          {!isFullscreenLoading && (
            <div className="lg:hidden">
              <Navigation />
            </div>
          )}
        </main>
      </div>

      {/* App-wide Chat Coach — hidden via CSS (never unmounted) on certain
          pages and during fullscreen loading, so the conversation survives. */}
      {!chatDisabled && (
        <div className={chatHiddenOnPage || isFullscreenLoading ? 'hidden' : ''}>
          <div
            className={`fixed bottom-28 lg:bottom-8 right-4 lg:right-8 z-60 transition-[transform,opacity] duration-300 motion-reduce:transition-none ${
              chromeHidden && !isChatOpen ? 'translate-y-32 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
            }`}
          >
            <button
              onClick={() => setIsChatOpen(!isChatOpen)}
              className={`
                w-9 h-9 lg:w-10 lg:h-10 rounded-full flex items-center justify-center shadow-lg shadow-growth/20
                transition-all duration-300 transform active:scale-95
                ${isChatOpen ? 'bg-mist text-growth' : 'bg-growth text-cloud'}
              `}
            >
              {isChatOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              ) : (
                <Icons.Sparkles className="w-4.5 h-4.5 lg:w-5 lg:h-5" />
              )}
            </button>
          </div>

          {/* Single chat instance for both breakpoints — mobile gets a
              full-screen overlay pinned to the visual viewport (inline style),
              desktop a floating panel (lg: classes). One instance means the
              conversation is shared across breakpoints and never lost to a
              container swap. */}
          {chatHasOpened && (
            <div
              className={`${isChatOpen ? '' : 'hidden'} z-60 overflow-hidden lg:fixed lg:inset-auto lg:bottom-28 lg:right-8 lg:w-96 lg:h-112 lg:rounded-2xl lg:shadow-2xl lg:shadow-growth/20 lg:border lg:border-white/50`}
              style={isDesktop ? undefined : viewportStyle || { position: 'fixed', inset: 0 }}
            >
              <ChatCoach onClose={() => setIsChatOpen(false)} />
            </div>
          )}
        </div>
      )}
    </div>
    </PageHeaderProvider>
  );
};
